import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import Theme from 'theme';
import { UseQueryResult } from 'react-query';
import { HrRefreshHistory, HrRefreshHistoryStatus, RefreshHistory } from 'pages/Dashboard/types/healthRecords.types';
import { calculateDaysDifference, formatEventDate, formatGoogleDate, GoogleDate } from 'utils/dateUtils';

interface HistoryDrawerProps {
    getHrRefreshHistory: UseQueryResult<HrRefreshHistory, unknown>;
}

function MedicalHistoryDrawer({ getHrRefreshHistory }: HistoryDrawerProps) {
  const currentDate = new Date();
  const [lastBoxHeight, setLastBoxHeight] = React.useState<number | null>(null);
  const lastBoxRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (lastBoxRef.current) {
      setLastBoxHeight(lastBoxRef.current.offsetHeight);
    }
  }, [getHrRefreshHistory.data]);

  const getEventDateText = (eventDate: GoogleDate, current: Date) => {
    const daysDiff = calculateDaysDifference(eventDate, current);

    if (daysDiff === 0) {
      return 'Today';
    }
    else if (daysDiff <= 59) {
      const text = daysDiff === 1 ? `${daysDiff} day ago` : `${daysDiff} days ago`;
      return (
        <Tooltip title={`${formatGoogleDate(eventDate, true)}`} arrow>
          <span>{text}</span>
        </Tooltip>
      );
    }
    else {
      return formatEventDate(eventDate, current);
    }
  };

  const getStatusText = (event: RefreshHistory) => {
    if (event.status === HrRefreshHistoryStatus.IN_PROGRESS) {
      return 'In progress';
    }
    if (event.hrCount === 1) {
      return '1 new record';
    }
    return `${event.hrCount} new records`;
  };

  return (

    <Box display='flex' flexDirection='column' flex={1} overflow='hidden'>
      <Box
        flex={1}
        maxHeight='calc(100% - 100px)'
        overflow='scroll'
      >
        <Box pl={2}>
          <Box position='relative' pl={3}>
            {getHrRefreshHistory.data?.refreshHistory.map((event, index) => {
              const statusText = getStatusText(event);
              const isLastEvent = index === (getHrRefreshHistory?.data
                ?.refreshHistory?.length ?? 0) - 1;
              return (
                <Box
                  key={`${event.scheduledOn.day}-${event.scheduledOn.month}-${event.scheduledOn.year}`}
                  position='relative'
                  pt={isLastEvent ? 1 : 2}
                  pb={3}
                  ref={isLastEvent ? lastBoxRef : null}
                >
                  {event.status === HrRefreshHistoryStatus.IN_PROGRESS ? (
                    <Refresh
                      fontSize='small'
                      sx={{
                        color: Theme.custom.colors.lightTextSecondary,
                        position: 'absolute',
                        left: -27,
                        top: '20%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: Theme.custom.colors.lightTextSecondary,
                        borderRadius: '50%',
                        position: 'absolute',
                        left: -24,
                        top: '22.5%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                      }}
                    />
                  )}
                  {index !== (getHrRefreshHistory?.data?.refreshHistory?.length ?? 0) - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -19,
                        top: 'calc(25% + 6px)',
                        height: 'calc(100% - 24px)',
                        width: 2,
                        backgroundColor: Theme.custom.colors.lightTextSecondary,
                      }}
                    />
                  )}
                  {getHrRefreshHistory?.data?.refreshHistory?.length > 1
                    && index === (getHrRefreshHistory?.data?.refreshHistory?.length ?? 0) - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -19,
                        top: 'calc(25% + 7px)',
                        height: lastBoxHeight ? `${lastBoxHeight - 54}px` : '36%',
                        width: 2,
                        backgroundColor: Theme.custom.colors.lightTextSecondary,
                      }}
                    />
                  )}
                  <Typography
                    color={Theme.custom.colors.lightTextSecondary}
                    variant='body2'
                    fontWeight={400}
                    mb={0.5}
                  >
                    {getEventDateText(event.scheduledOn, currentDate)}
                  </Typography>
                  {event.status === HrRefreshHistoryStatus.INTIAL_REQUEST
                     && event.hrCount === 0 ? (
                      null
                    ) : (
                      <Typography variant='body1' fontWeight={400} mb={0.5}>
                        {statusText}
                      </Typography>
                    )}
                  <Typography color={Theme.custom.colors.lightTextSecondary} variant='body2' className='fs-mask'>
                    {event.status === HrRefreshHistoryStatus.INTIAL_REQUEST
                      ? 'Initial request' : `Requested by 
                    ${event.scheduledBy?.firstName} ${event.scheduledBy?.lastName}`}
                  </Typography>
                  {index === 0 && event.status === HrRefreshHistoryStatus.IN_PROGRESS && (
                    <Typography variant='body2' color={Theme.custom.colors.lightTextSecondary}>
                      This may take up to 36 hours.
                      A notification will be sent when the refresh completes.
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default MedicalHistoryDrawer;
